// Bot entry point. Per tick, for each non-human-controlled skater:
//   1. Classify the puck-state role (carrier / support / defender / chaser).
//   2. Pick the active behavior:
//        - carrier         → fillCarrierInput
//        - defender + closest to carrier  → fillDefenderInput (pressure)
//        - chaser + closest to loose puck → fillChaserInput
//        - everything else (support, defender-cover, non-closest chaser)
//          → fillAnchorInput, which positions the bot by (role × situation)
//
// Phase G slice 2 makes the anchor situation-aware, so non-engaged bots
// shape the team correctly across forecheck, breakout, neutral-zone, and
// defensive-zone defense — instead of all collapsing to a single
// "offset from carrier" or "between own net and puck" formula.
//
// The returned array is pooled — callers must consume within the same frame
// and not hold references across calls. Zero allocation in steady state.

import { type PlayerInput, playerId } from "@arcade-hockey/shared";
import type { MatchState, PlayerState } from "@arcade-hockey/sim";
import { fillAnchorInput } from "./behaviors/anchor.js";
import { fillCarrierInput } from "./behaviors/carrier.js";
import { fillChaserInput } from "./behaviors/chaser.js";
import { fillDefenderInput } from "./behaviors/defender.js";
import { fillCenterFaceoffInput } from "./faceoff.js";
import { fillChase } from "./geometry/chase.js";
import { isClosestTeammateTo, teammateRankByDistance } from "./geometry/closest-teammate.js";
import { classifyRole } from "./roles.js";

const INPUT_POOL: PlayerInput[] = [];
const OUTPUT_BUFFER: PlayerInput[] = [];
const SENTINEL_PLAYER_ID = playerId("");

function poolEntry(idx: number): PlayerInput {
  let entry = INPUT_POOL[idx];
  if (entry === undefined) {
    entry = {
      tick: 0,
      seq: 0,
      seatId: "P1",
      playerId: SENTINEL_PLAYER_ID,
      moveX: 0,
      moveY: 0,
      sprint: 0,
      buttons: 0,
    };
    INPUT_POOL[idx] = entry;
  }
  return entry;
}

function resetInput(input: PlayerInput, player: PlayerState, currentTick: number): void {
  input.tick = currentTick;
  input.seq = 0;
  input.seatId = "P1";
  input.playerId = player.id;
  input.moveX = 0;
  input.moveY = 0;
  input.sprint = 0;
  input.buttons = 0;
}

function isHumanControlled(state: MatchState, id: PlayerState["id"]): boolean {
  for (const team of state.control) {
    const cbh = team.controlledByHuman;
    if (cbh.P1 === id) return true;
    if (cbh.P2 === id) return true;
  }
  return false;
}

function fillByRole(input: PlayerInput, player: PlayerState, state: MatchState): void {
  const role = classifyRole(player, state);
  switch (role) {
    case "carrier":
      fillCarrierInput(input, player, state);
      return;
    case "support":
      // Slice G/2: support is no longer a single "X meters ahead of
      // carrier in our lane" formula. Non-carrier teammates anchor by
      // (role × situation) so the team takes the right shape for the
      // current zone + possession (cycle, breakout, etc.).
      fillAnchorInput(input, player, state);
      return;
    case "defender": {
      // classifyRole already proved ownerId is non-null and resolves to
      // an opponent — the lookup is safe.
      const carrier = state.playersById.get(state.puck.ownerId as PlayerState["id"]);
      if (
        carrier !== undefined &&
        isClosestTeammateTo(player, carrier.pos.x, carrier.pos.y, state)
      ) {
        // Closest defender pressures the carrier with stick-lift attempts.
        fillDefenderInput(input, player, carrier, state);
      } else {
        // Everyone else covers their (role × situation) anchor —
        // forwards collapse into the slot, weak-side D goes net front, etc.
        fillAnchorInput(input, player, state);
      }
      return;
    }
    case "chaser":
      // Loose puck. F1 (closest) full-chases the puck. F2 (second-closest)
      // trails by ~3 m IF it's within F2_TRAIL_RANGE of the puck — that's
      // the real-hockey "support the recovery" position. F2 farther out,
      // and F3+, anchor by role/situation so the team doesn't dogpile.
      {
        const F2_TRAIL_RANGE = 5;
        const rank = teammateRankByDistance(player, state.puck.pos.x, state.puck.pos.y, state);
        if (rank === 1) {
          fillChaserInput(input, player, state);
        } else if (rank === 2) {
          const myDx = player.pos.x - state.puck.pos.x;
          const myDy = player.pos.y - state.puck.pos.y;
          const myDist = Math.hypot(myDx, myDy);
          if (myDist < F2_TRAIL_RANGE) {
            // Close enough to be the trailer — chase a point 3 m behind
            // the puck along its velocity vector (or the puck itself when
            // it's near-stationary) so we collect bad bounces.
            const vlen = Math.hypot(state.puck.vel.x, state.puck.vel.y);
            let trailX = state.puck.pos.x;
            let trailY = state.puck.pos.y;
            if (vlen > 0.5) {
              trailX -= (state.puck.vel.x / vlen) * 3;
              trailY -= (state.puck.vel.y / vlen) * 3;
            }
            fillChase(input, player, trailX, trailY, 0.5);
          } else {
            fillAnchorInput(input, player, state);
          }
        } else {
          fillAnchorInput(input, player, state);
        }
      }
      return;
  }
}

/**
 * Produce one PlayerInput per non-human-controlled skater. Returns a shared,
 * pooled array — callers must consume it within the same frame and not hold
 * references across calls. Zero allocation in steady state.
 */
export function tickBots(state: MatchState, currentTick: number): PlayerInput[] {
  OUTPUT_BUFFER.length = 0;
  const inFaceoff = state.clock.phase === "Faceoff";
  // Phase H Slice 1 — after a faceoff drop the centres are "tied up
  // in the draw" for ~1 second. The sim blocks them from claiming
  // the puck (possession.ts) and we mirror that here in bot
  // dispatch: centres anchor instead of chasing during the lockout.
  // Without this, the centre right next to the dot is always the
  // closest chaser and snags the pop the moment the sim-side
  // lockout expires.
  const inCenterFaceoffLockout = currentTick < state.puck.faceoffCenterLockoutUntilTick;
  let idx = 0;
  for (const player of state.players) {
    if (isHumanControlled(state, player.id)) continue;
    // Slice 8 — benched skaters (OT wings) have no input. The sim
    // pins them at the bench; emitting a zeroed input here would
    // still be harmless but a no-op input is cleaner.
    if (player.benched) continue;
    const input = poolEntry(idx);
    resetInput(input, player, currentTick);
    // Phase H Slice 1: bot-controlled centres signal their faceoff intent
    // via input.moveX during the Faceoff phase. The sim collects intents
    // in Step 4a and resolves them at the Faceoff → Play transition.
    // Non-centre bots stay frozen during Faceoff (their inputs are
    // ignored by Step 5's isLive gate).
    if (inFaceoff && player.position === "C") {
      fillCenterFaceoffInput(input, player, state);
      OUTPUT_BUFFER.push(input);
      idx += 1;
      continue;
    }
    // Centres during the post-faceoff lockout: route to anchor so
    // the targeted teammate (winger / defenseman) becomes the
    // closest free skater to the puck and chases naturally.
    if (inCenterFaceoffLockout && player.position === "C") {
      fillAnchorInput(input, player, state);
      OUTPUT_BUFFER.push(input);
      idx += 1;
      continue;
    }
    fillByRole(input, player, state);
    OUTPUT_BUFFER.push(input);
    idx += 1;
  }
  return OUTPUT_BUFFER;
}

export { classifyRole } from "./roles.js";
export type { BotRole } from "./roles.js";
